import React, { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';

import { catalogApi } from 'api/catalog';
import { searchApi as api } from 'api/search';
import { shopperMarketingApi } from 'api/shopperMarketing';
import { DataTable, FormComponent } from 'shared';

function SearchBoostComponent() {
    const [rows, setRows] = useState([]);
    const [addItemModalOpen, setAddItemModalOpen] = useState(false);
    const [notification, setNotification] = useState(null);
    const [vendorPromotions, setVendorPromotions] = useState([]);
    const [selectedVendorPromotion, setSelectedVendorPromotion] = useState('');
    const [searchString, setSearchString] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [catalogItems, setCatalogItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemSearchTerm, setItemSearchTerm] = useState('');

    const getBoostedSearches = async () => {
        try {
            const response = await api.getBoostedSearches();

            if (response.status === true) {
                setRows(response.data.rows);
            } else {
                setNotification({
                    type: 'error',
                    message: 'Failed to load boosted searches',
                });
            }
        } catch (error) {
            console.error('Error fetching boosted searches:', error);
            setNotification({
                type: 'error',
                message: 'Error loading boosted searches',
            });
        }
    };

    const getVendorPromotions = async () => {
        try {
            const response = await shopperMarketingApi.getVendorPromotions();
            if (response.status === true) {
                // Filter to only include promotions with isBoostedSearch flag
                const boostedSearchPromotions = (response.data.rows || []).filter(
                    (promotion) => promotion.isBoostedSearch === true,
                );
                setVendorPromotions(boostedSearchPromotions);
            }
        } catch (error) {
            console.error('Error fetching vendor promotions:', error);
        }
    };

    const getCatalogItems = async () => {
        try {
            const response = await catalogApi.getItemInformation(true); // active items only
            if (response.status === true) {
                setCatalogItems(response.data.items || []);
            }
        } catch (error) {
            console.error('Error fetching catalog items:', error);
        }
    };

    const handleSubmit = async () => {
        if (!searchString.trim() || !selectedVendorPromotion || !selectedItem) {
            setNotification({
                type: 'error',
                message: 'Please fill in all required fields',
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await api.createBoostedSearch(
                searchString.trim(),
                selectedVendorPromotion,
                selectedItem.externalSku,
            );

            if (response.status === true) {
                setNotification({
                    type: 'success',
                    message: 'Boosted search created successfully',
                });
                setAddItemModalOpen(false);
                setSearchString('');
                setSelectedVendorPromotion('');
                setSelectedItem(null);
                getBoostedSearches(); // Refresh the list
            } else {
                setNotification({
                    type: 'error',
                    message: response.msg || 'Failed to create boosted search',
                });
            }
        } catch (error) {
            console.error('Error creating boosted search:', error);
            setNotification({
                type: 'error',
                message: error.msg || 'Error creating boosted search',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm(`Are you sure you want to delete boosted search #${id}?`)) {
            try {
                const response = await api.deleteBoostedSearch(id);
                if (response.status === true) {
                    setNotification({
                        type: 'success',
                        message: 'Boosted search deleted successfully',
                    });
                    getBoostedSearches(); // Refresh list
                } else {
                    setNotification({
                        type: 'error',
                        message: response.msg || 'Failed to delete boosted search',
                    });
                }
            } catch (error) {
                setNotification({
                    type: 'error',
                    message: error.msg || 'Error deleting boosted search',
                });
            }
        }
    };

    const handleCloseModal = () => {
        setAddItemModalOpen(false);
        setSearchString('');
        setSelectedVendorPromotion('');
        setSelectedItem(null);
        setItemSearchTerm('');
        setNotification(null);
    };

    const handleItemSelect = (item) => {
        setSelectedItem(item);
        if (item) {
            setSearchString(`${item.name || item.itemName || 'Unknown'} (SKU: ${item.externalSku})`);
        }
    };

    useEffect(() => {
        getBoostedSearches();
        getVendorPromotions();
        getCatalogItems();
    }, []);

    // Filter catalog items based on search term
    const filteredItems = catalogItems
        .filter(
            (item) =>
                item.name?.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
                item.itemName?.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
                item.externalSku?.toString().includes(itemSearchTerm) ||
                item.brand?.toLowerCase().includes(itemSearchTerm.toLowerCase()),
        )
        .slice(0, 50); // Limit to 50 results for performance

    const columns = [
        { accessor: 'id', Header: 'ID', width: 50 },
        { accessor: 'searchString', Header: 'Search String' },
        { accessor: 'vendorPromotionId', Header: 'Vendor Promotion ID' },
        { accessor: 'externalSku', Header: 'External SKU' },
        { accessor: 'itemName', Header: 'Item Name' },
        { accessor: 'brand', Header: 'Brand' },
        { accessor: 'vendorName', Header: 'Vendor Name' },
        { accessor: 'startWeek', Header: 'Start Week' },
        { accessor: 'startYear', Header: 'Start Year' },
        { accessor: 'endWeek', Header: 'End Week' },
        { accessor: 'endYear', Header: 'End Year' },
        { accessor: 'authUser', Header: 'Auth User' },
        {
            Header: 'Actions',
            accessor: 'actions',
            Cell: ({ row }) => (
                <Button variant="contained" color="secondary" onClick={() => handleDelete(row.original.id)}>
                    Delete
                </Button>
            ),
        },
    ];

    return (
        <Grid container>
            {!addItemModalOpen && notification && (
                <Alert severity={notification.type} style={{ marginBottom: '1rem', width: '100%' }}>
                    {notification.message}
                </Alert>
            )}
            <Button
                id="AddItem"
                style={{ margin: '0 auto' }}
                onClick={async (e) => {
                    e.stopPropagation();
                    setNotification(null);
                    setAddItemModalOpen(true);
                }}
            >
                Add Item
            </Button>
            <DataTable
                editable
                columns={columns}
                data={rows}
                saveEdit={async ({ field, id, value }) => {
                    console.log('saveEdit', id, field, value);
                    // TODO: Implement save edit functionality
                }}
            />

            {/* Add Item Modal */}
            <Dialog open={addItemModalOpen} onClose={handleCloseModal} maxWidth="xl" fullWidth>
                <DialogTitle>Add Boosted Search</DialogTitle>
                <DialogContent>
                    {notification && (
                        <Alert severity={notification.type} style={{ marginBottom: '1rem' }}>
                            {notification.message}
                        </Alert>
                    )}
                    <Autocomplete
                        options={filteredItems}
                        getOptionLabel={(option) =>
                            `${option.name || option.itemName || 'Unknown'} (SKU: ${option.externalSku})`
                        }
                        value={selectedItem}
                        onChange={(event, newValue) => handleItemSelect(newValue)}
                        onInputChange={(event, newInputValue) => setItemSearchTerm(newInputValue)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Search for Item"
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                required
                                placeholder="Start typing to search items..."
                            />
                        )}
                        renderOption={(option) => (
                            <div>
                                <strong>{option.name || option.itemName || 'Unknown'}</strong>
                                <br />
                                <small>
                                    SKU: {option.externalSku} | Brand: {option.brand || 'N/A'}
                                </small>
                            </div>
                        )}
                    />

                    <TextField
                        margin="dense"
                        label="Search String"
                        type="text"
                        fullWidth
                        value={searchString}
                        onChange={(e) => setSearchString(e.target.value)}
                        required
                        helperText="This is the search term that will be boosted. You can modify it from the selected item."
                    />

                    <FormControl fullWidth margin="dense" required>
                        <InputLabel>Vendor Promotion</InputLabel>
                        <Select
                            value={selectedVendorPromotion}
                            onChange={(e) => setSelectedVendorPromotion(e.target.value)}
                            label="Vendor Promotion"
                        >
                            {vendorPromotions.map((promotion) => (
                                <MenuItem key={promotion.id} value={promotion.id}>
                                    {promotion.id} - {promotion.vendorName || 'Unknown Vendor'}({promotion.startWeek}/
                                    {promotion.startYear} - {promotion.endWeek}/{promotion.endYear})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" disabled={isSubmitting}>
                        {isSubmitting ? 'Creating...' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}

export { SearchBoostComponent };
